import type { ApiErrorBody, FieldError } from "../../shared/api.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors: FieldError[] = [],
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
  get isNetwork() {
    return this.status === 0;
  }
}

let csrfToken = "";
export const setCsrfToken = (t: string) => {
  csrfToken = t;
};

/** Listeners for responses that invalidate protected client state (session ended, access cut off). */
type Listener = (e: ApiError) => void;
const listeners = new Set<Listener>();
export const onAuthFailure = (l: Listener) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

const NETWORK_MESSAGE = "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.";

export async function api<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(method !== "GET" ? { "Content-Type": "application/json", "X-CSRF-Token": csrfToken } : {}),
        ...headers,
      },
      body: method !== "GET" ? JSON.stringify(body ?? {}) : undefined,
    });
  } catch {
    throw new ApiError(0, "NETWORK", NETWORK_MESSAGE);
  }
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as (ApiErrorBody & { data?: unknown }) | null;
  if (!res.ok) {
    const e = json?.error;
    const err = new ApiError(res.status, e?.code ?? "INTERNAL", e?.message ?? "Ocorreu um erro inesperado.", e?.field_errors ?? [], e?.details);
    // /auth/me answering 401 just means "anonymous visitor" (public invite/reset pages), not an ended session.
    if (path !== "/auth/me" && ["UNAUTHENTICATED", "PLAN_ACCESS_EXPIRED", "MEMBERSHIP_REVOKED"].includes(err.code)) listeners.forEach((l) => l(err));
    throw err;
  }
  return json as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body?: unknown, headers?: Record<string, string>) => api<T>("POST", path, body, headers);
export const put = <T>(path: string, body?: unknown) => api<T>("PUT", path, body);
export const patch = <T>(path: string, body?: unknown) => api<T>("PATCH", path, body);

/** Binary export download (POST with CSRF); errors come back as structured JSON. */
export async function download(path: string, body: unknown): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError(0, "NETWORK", NETWORK_MESSAGE);
  }
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as ApiErrorBody | null;
    const err = new ApiError(res.status, json?.error.code ?? "INTERNAL", json?.error.message ?? "Não foi possível gerar o arquivo.");
    if (["UNAUTHENTICATED", "PLAN_ACCESS_EXPIRED", "MEMBERSHIP_REVOKED"].includes(err.code)) listeners.forEach((l) => l(err));
    throw err;
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "exportacao";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Maps server field errors into react-hook-form. */
export function applyFieldErrors(err: unknown, setError: (name: never, e: { message: string }) => void) {
  if (err instanceof ApiError) for (const f of err.fieldErrors) setError(f.path as never, { message: f.message });
}
